// Character lettering (§4.8): every voice has its own look so the source of each message is clear.
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Rich } from './RichText.tsx';

export type Who = 'hilda' | 'echo' | 'mei' | 'kirov' | 'm' | 'bureau' | 'guide' | 'operator';

const NAMES: Record<Who, string> = {
  hilda: 'Hilda Solnes',
  echo: 'ECHO',
  mei: 'Mei Tran',
  kirov: 'Director Kirov',
  m: 'M.',
  bureau: 'The Bureau',
  guide: 'The Guide',
  operator: 'You',
};

export const speakerName = (w: Who): string => NAMES[w];

/** Apply a case transform outside {chem} spans (formulas keep their case). */
const outsideChem = (text: string, f: (s: string) => string) =>
  text.split(/(\{[^{}]*\})/).map((part) => (part.startsWith('{') ? part : f(part))).join('');

/** Normalise text to a character's lettering rules. */
export function letter(who: Who, text: string): string {
  if (who === 'echo') {
    const t = outsideChem(text.replace(/^echo\/\s*/, ''), (s) => s.toLowerCase().replace(/\.(?=\s|$)/g, '')).trim();
    return `echo/ ${t}`;
  }
  if (who === 'mei') return outsideChem(text, (s) => s.toLowerCase());
  if (who === 'bureau') return outsideChem(text, (s) => s.toUpperCase());
  return text;
}

export function Bubble(props: { who: Who; text: string; children?: ComponentChildren | undefined; class?: string | undefined }) {
  const { who } = props;
  return (
    <div class={`bubble bubble-${who}${props.class ? ` ${props.class}` : ''}`}>
      <span class="bubble-who">{who === 'hilda' ? 'H. SOLNES · FOREMAN' : NAMES[who]}</span>
      <span class="bubble-text">
        <Rich text={letter(who, props.text)} />
      </span>
      {who === 'hilda' && <span class="bubble-sign">— H. Solnes, Foreman.</span>}
      {who === 'm' && <span class="bubble-sign">-- M.</span>}
      {props.children}
    </div>
  );
}

export function BureauNotice(props: { title: string; children: ComponentChildren }) {
  return (
    <div class="bureau-notice" role="note">
      <div class="bureau-head">BUREAU OF MATERIAL CONTINUITY · {props.title.toUpperCase()}</div>
      <div class="bureau-body">{props.children}</div>
    </div>
  );
}

export function WirePacket(props: { children: ComponentChildren }) {
  return (
    <div class="wire-packet" role="note">
      <div class="wire-head">// WIRE PACKET</div>
      <div class="wire-body">{props.children}</div>
      <div class="wire-sign">-- M.</div>
    </div>
  );
}

export function PenroseMessage(props: { children: ComponentChildren }) {
  return (
    <div class="penrose-msg" role="note">
      <div class="penrose-head">PENROSE LLC</div>
      <div class="penrose-body">{props.children}</div>
      <div class="penrose-sign">V. Kirov, Director</div>
    </div>
  );
}

export interface EchoLine {
  id: number;
  text: string;
  kind: 'echo' | 'hint' | 'bureau' | 'info';
  rung?: number | undefined;
}

/** The docked ECHO terminal: hints and readouts (Prologue: the Bureau Help Desk). */
export function EchoTerminal(props: {
  lines: EchoLine[];
  onHint?: (() => void) | undefined;
  hintLabel?: string | undefined;
  hintDisabled?: boolean | undefined;
  mode: 'echo' | 'bureau';
  onTapEcho?: (() => void) | undefined;
  compact?: boolean | undefined;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [props.lines.length]);
  return (
    <section class={`echo-terminal echo-${props.mode}${props.compact ? ' compact' : ''}`} aria-label={props.mode === 'echo' ? 'ECHO terminal' : 'Bureau Help Desk'}>
      {/* A focusable log, so keyboard users can scroll back through earlier lines (WCAG 2.1.1). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div class="echo-lines" ref={ref} role="log" aria-live="polite" aria-label={props.mode === 'echo' ? 'ECHO log' : 'Help Desk log'} tabIndex={0}>
        {props.lines.length === 0 && (
          <p class="echo-line echo-idle">{props.mode === 'echo' ? 'echo/ standing by' : 'BUREAU HELP DESK. ASSISTANCE IS LOGGED.'}</p>
        )}
        {props.lines.map((l) => (
          <p key={l.id} class={`echo-line echo-${l.kind}`}>
            {l.rung && <span class="echo-rung">[{['nudge', 'method', 'worked step'][l.rung - 1]}] </span>}
            <Rich text={props.mode === 'echo' || l.kind === 'echo' ? letter('echo', l.text) : letter('bureau', l.text)} />
          </p>
        ))}
      </div>
      <div class="echo-actions">
        {props.onTapEcho && props.mode === 'echo' && (
          <button type="button" class="echo-badge" onClick={props.onTapEcho} aria-label="ECHO">
            ▮ ECHO
          </button>
        )}
        {props.onHint && (
          <button type="button" class="echo-hint-btn" onClick={props.onHint} disabled={props.hintDisabled}>
            {props.hintLabel ?? (props.mode === 'echo' ? 'echo/ hint' : 'REQUEST ASSISTANCE')}
          </button>
        )}
      </div>
    </section>
  );
}
